import { cx } from '@/lib/cx';
import { PIPELINE_ORDER, statusDef, type PipelineState } from './status';

/** The five open stages, New to Ready; the rest of PIPELINE_ORDER are closed stages. */
const OPEN_STAGES = PIPELINE_ORDER.slice(0, 5) as readonly string[];
const CLOSED_STAGES = PIPELINE_ORDER.slice(5) as readonly string[];

export interface StageTrackProps {
  stage: PipelineState | string | null | undefined;
  /** Stage name and "2 of 5" under the bar. Default true; pass false for the slim card version. */
  label?: boolean;
  className?: string;
}

/**
 * Progress through the pipeline: five segments filled up to the current stage. Closed stages
 * (Disqualified, Cancelled, Archived) show empty, struck through segments and their name.
 */
export function StageTrack({ stage, label = true, className }: StageTrackProps) {
  const def = statusDef('pipeline', stage);
  const at = stage ? OPEN_STAGES.indexOf(stage) : -1;
  const closed = Boolean(stage && CLOSED_STAGES.includes(stage));
  const ready = at === OPEN_STAGES.length - 1;
  const step = at >= 0 ? `${at + 1} of ${OPEN_STAGES.length}` : null;
  const text = step ? `${def.label}, stage ${step}` : def.label;
  return (
    <span
      className={cx('stage-track', className)}
      data-closed={closed || undefined}
      data-ready={ready || undefined}
      role="img"
      aria-label={`Stage: ${text}`}
      title={label ? undefined : text}
    >
      <span className="stage-track-bar" aria-hidden>
        {OPEN_STAGES.map((s, i) => (
          <span key={s} className="stage-track-seg" data-on={i <= at ? true : undefined} />
        ))}
      </span>
      {label ? (
        <span className="stage-track-label" aria-hidden>
          <span>{def.label}</span>
          {step ? <span className="num">{step}</span> : closed ? <span>Closed</span> : null}
        </span>
      ) : null}
    </span>
  );
}
